import { NgTemplateOutlet } from '@angular/common';
import {
  Attribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  HostListener,
  Input,
  input,
  output,
  Renderer2,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { Icon } from '../../icon';
import { IconComponent } from '../../icon/icon.component';
import { CheckboxComponent } from '../checkbox/checkbox.component';

export type SelectOption<T, U> = {
  value: T;
  label?: string;
  disabled?: boolean;
  /** Translated string label displayed above each category. Keep empty to not show any category */
  categoryLabel?: string;
  /** Allow to append additional data to the option for better context */
  meta?: U;
};

@Component({
  selector: 'smw-select',
  imports: [NgTemplateOutlet, IconComponent, CheckboxComponent],
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: SelectComponent,
      multi: true,
    },
  ],
})
export class SelectComponent<T = unknown, U = unknown> implements ControlValueAccessor {
  private _options: SelectOption<T, U>[] = [];
  @Input() set options(value: SelectOption<T, U>[]) {
    value = this.removeDuplicatedCategories(value);
    this._options = this.sortOptionsByCategory(value);
  }

  get options() {
    return this._options;
  }

  label = input('');
  placeholder = input('');
  valueKey = input<keyof T | undefined>(undefined);
  invalid = input(false);
  /** If true, allow to multi select multiples elements with a checkbox */
  multi = input(false);
  categoryLabels = input<Record<string, string>>({});
  categoryOrders = input<Record<string, number>>({});

  icon = Icon.selectArrow;

  @ViewChild('selectContainer', { static: true, read: ElementRef })
  selectField!: ElementRef<HTMLInputElement>;

  @ContentChild('optionTemplate', { read: TemplateRef })
  optionTemplate!: TemplateRef<unknown>;

  inputId = signal('');
  selectedOptions = signal<Array<SelectOption<T, U>>>([]);
  isMenuDisplayed = signal(false);
  isDisabled = signal(false);

  onChange: (value: T | T[keyof T]) => void = () => {};
  onTouched: () => void = () => {};
  closeEvent = output();
  onValidationChange!: () => void;

  constructor(
    @Attribute('id') public id: string,
    private element: ElementRef,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef,
  ) {
    if (this.id) {
      this.inputId.set(this.id);
      this.renderer.removeAttribute(this.element.nativeElement, 'id');
    }
  }

  isOptionSelected(option: SelectOption<T, U>): boolean {
    return this.selectedOptions().some((selectedOption) => selectedOption.value === option.value);
  }

  writeValue(value: T | T[keyof T] | null): void {
    const options = this._options;

    if (!options.length) {
      this.selectedOptions.set([]);
      return;
    }

    if (value === undefined || value === null) {
      this.selectedOptions.set([]);
      return;
    }

    const valueKey = this.valueKey();
    let selectedOption: SelectOption<T, U> | undefined;

    if (valueKey) {
      selectedOption = options.find((item) => item.value[valueKey] === value);
    } else {
      // If we're dealing with objects and value is an object, we need to do a deep comparison
      if (typeof value === 'object') {
        selectedOption = options.find((item) => {
          if (typeof item.value === 'object') {
            // Compare all properties of the objects
            return Object.keys(item.value as object).every(
              (key) =>
                (item.value as Record<string, unknown>)[key] ===
                (value as Record<string, unknown>)[key],
            );
          }
          return item.value === value;
        });
      } else {
        selectedOption = options.find((item) => item.value === value);
      }
    }

    if (selectedOption) {
      this.selectedOptions.set([selectedOption]);
    } else {
      this.selectedOptions.set([]);
    }
  }

  registerOnChange(fn: (value: T | T[keyof T]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  select(option: SelectOption<T, U>, checked: boolean): void {
    if (option.disabled) {
      return;
    }

    if (this.onTouched) {
      this.onTouched();
    }

    if (this.multi()) {
      if (checked) {
        this.selectedOptions.update((options) => [...options, option]);
      } else {
        this.selectedOptions.update((options) =>
          options.filter((item) => item.value !== option.value),
        );
      }
    } else {
      this.selectedOptions.set([option]);
    }

    const valueKey = this.valueKey();

    if (this.onChange) {
      this.onChange(valueKey ? option.value[valueKey] : option.value);
    }

    if (!this.multi()) {
      this.close();
    }
  }

  /** Add a new option
   * @param option The option to add
   * @param selectIt If true, the option will be selected when added
   */
  addOption(option: SelectOption<T, U>, selectIt: boolean): void {
    this.options.push(option);
    this._options = [...this._options];

    if (selectIt) {
      this.select(option, true);
    }

    this.cdr.markForCheck();
  }

  close(): void {
    if (this.isMenuDisplayed()) {
      this.isMenuDisplayed.set(false);
      this.closeEvent.emit();
    }
  }

  @HostListener('document:mousedown', ['$event'])
  onClickOutside(event: Event): void {
    if (!this.element.nativeElement.contains(event.target)) {
      this.close();
    }
  }

  /**
   * Sorts options by category so items with the same category are grouped together
   */
  private sortOptionsByCategory(options: SelectOption<T, U>[]): SelectOption<T, U>[] {
    if (!options.length) {
      return options;
    }

    const orders = this.categoryOrders();

    return [...options].sort((a, b) => {
      // If categories are different, sort by category
      if (a.categoryLabel !== b.categoryLabel) {
        // If both have categories, use categoryOrders if available
        if (a.categoryLabel && b.categoryLabel) {
          const orderA = orders[a.categoryLabel] ?? Number.MAX_SAFE_INTEGER;
          const orderB = orders[b.categoryLabel] ?? Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        }
        // Put undefined categories at the end
        if (!a.categoryLabel) {
          return 1;
        }
        if (!b.categoryLabel) {
          return -1;
        }
        return a.categoryLabel.localeCompare(b.categoryLabel);
      }

      // If categories are the same, maintain original order
      return 0;
    });
  }

  private removeDuplicatedCategories(options: SelectOption<T, U>[]): SelectOption<T, U>[] {
    const seenCategories = new Set<string>();
    options.forEach((option) => {
      if (option.categoryLabel && seenCategories.has(option.categoryLabel)) {
        option.categoryLabel = undefined;
      } else {
        if (option.categoryLabel) {
          seenCategories.add(option.categoryLabel);
        }
      }
    });

    return options;
  }
}
